import React, {useRef, useState} from 'react'

export interface ConversationTabItem {
  id: string
  title: string
  isRunning: boolean
  isActive: boolean
  /** Turns arrived that this client hasn't loaded (another device, or a cloud continuation). */
  hasUpdates?: boolean
  /** Saved conversations only: a draft has no history entry to rename yet. */
  canRename?: boolean
}

interface ConversationTabsProps {
  tabs: ConversationTabItem[]
  onSelect: (id: string) => void
  onClose: (id: string) => void
  /** Same rename as the history list, so tab and history stay in sync. */
  onRename?: (id: string, title: string) => void
}

interface TabTitleEditorProps {
  initial: string
  onDone: (title: string | null) => void
}

/** Inline title input: Enter or blur saves, Escape cancels. Reports `null` when nothing changed. */
const TabTitleEditor: React.FC<TabTitleEditorProps> = ({initial, onDone}) => {
  const [value, setValue] = useState(initial)
  const finished = useRef(false)

  const finish = (save: boolean) => {
    if (finished.current) return
    finished.current = true
    const next = value.trim()
    onDone(save && next && next !== initial ? next : null)
  }

  return (
    <input
      type='text'
      className='ai-chat-v2-sidebar__tab-edit-input'
      aria-label='Rename chat'
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={() => finish(true)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          finish(true)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          finish(false)
        }
      }}
    />
  )
}

export const ConversationTabs: React.FC<ConversationTabsProps> = ({
  tabs,
  onSelect,
  onClose,
  onRename,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null)

  return (
    <div className='ai-chat-v2-sidebar__tabs' data-testid='omics-ai-chat-tabs'>
      <div className='ai-chat-v2-sidebar__tabs-scroll'>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`ai-chat-v2-sidebar__tab ${tab.isActive ? 'active' : ''} ${tab.isRunning ? 'is-running' : ''}`}
          >
            {editingId === tab.id ? (
              <TabTitleEditor
                initial={tab.title}
                onDone={(title) => {
                  setEditingId(null)
                  if (title) onRename?.(tab.id, title)
                }}
              />
            ) : (
              <button
                type='button'
                className='ai-chat-v2-sidebar__tab-main'
                onClick={() => onSelect(tab.id)}
                onDoubleClick={() => {
                  if (onRename && tab.canRename) setEditingId(tab.id)
                }}
                title={tab.canRename && onRename ? `${tab.title || 'Untitled'} (double-click to rename)` : tab.title || 'Untitled'}
              >
                {tab.isRunning ? <span className='ai-chat-v2-sidebar__tab-spinner' aria-hidden /> : null}
                {!tab.isRunning && tab.hasUpdates ? (
                  <span className='ai-chat-v2-sidebar__tab-dot' title='New messages' aria-label='New messages' />
                ) : null}
                <span className='ai-chat-v2-sidebar__tab-title'>{tab.title || 'New chat'}</span>
              </button>
            )}
            <button
              type='button'
              className='ai-chat-v2-sidebar__tab-close'
              aria-label='Close tab'
              disabled={tab.isRunning}
              title={tab.isRunning ? 'Stop the run before closing this tab' : 'Close tab'}
              onClick={(e) => {
                e.stopPropagation()
                onClose(tab.id)
              }}
            >
              <i className='fas fa-xmark' />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
